import { Team, RandomizationHistory } from '../types';
import { PE_TEAM, CE_TEAM, SEDI } from '../data/participants';

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
export function shuffle<T>(array: readonly T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = result[i]!;
    result[i] = result[j]!;
    result[j] = temp;
  }
  return result;
}

/**
 * Select the best team for SeDi based on pairing history
 * Returns the index of the team with the lowest pairing score
 */
function selectTeamForSeDi(teams: Team[], history: RandomizationHistory): number {
  const scores = teams.map(team => {
    let score = 0;
    for (const member of team.members) {
      const pairKey = `${SEDI}-${member.name}`;
      score += history.participantPairings[pairKey] || 0;
    }
    return score;
  });
  
  // Return index of team with lowest score (least previous pairings with SeDi)
  return scores.indexOf(Math.min(...scores));
}

/**
 * Generate 4 randomized teams with constraints:
 * - CE members are statically distributed (one per team, always in same order)
 * - PE members are randomized across teams
 * - SeDi is assigned to exactly one team
 * - Optimizes for diversity based on pairing history
 */
export function generateTeams(history: RandomizationHistory): Team[] {
  // 1. Shuffle only PE members (CE members are static)
  const shuffledPE = shuffle([...PE_TEAM]);
  
  // 2. Create teams with static CE distribution
  const teams: Team[] = [
    { id: 'team-1', name: 'Team 1', members: [] },
    { id: 'team-2', name: 'Team 2', members: [] },
    { id: 'team-3', name: 'Team 3', members: [] },
    { id: 'team-4', name: 'Team 4', members: [] }
  ];
  
  // 3. Assign CE members statically (one per team, always same assignment)
  for (let i = 0; i < 4; i++) {
    const team = teams[i]!;
    const ceMember = CE_TEAM[i]!;
    team.members.push({ name: ceMember, team: 'CE' });
  }
  
  // 4. Assign randomized PE members (one per team)
  for (let i = 0; i < 4; i++) {
    const team = teams[i]!;
    const peMember = shuffledPE[i]!;
    team.members.push({ name: peMember, team: 'PE' });
  }
  
  // 5. Assign SeDi to the team with lowest pairing scores (optimization)
  const teamIndex = selectTeamForSeDi(teams, history);
  teams[teamIndex]!.members.push({ name: SEDI, team: 'Special' });
  
  return teams;
}
